import { NotificationTheme } from '@wix/design-system';
import { sanitizeHtml } from '@wix/wix-chatbot-common/browser';
import { useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import React, { FC, memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { TLocalesKeys } from '../../../types';
import {
  logChatbotStatusBannerStatusBannerClicked,
  logChatbotStatusBannerStatusBannerDismissed,
  logChatbotStatusBannerStatusBannerShowed,
} from '../../bi';
import { biLogger } from '../../bi/biLogger';
import { SectionEventSource, useBiOnVisibleItem } from '../../bi/hooks/useBiOnVisibleItem';
import { ButtonClicked } from '../../bi/types/buttonClicked';
import { headerActions } from '../../common/reducers/header';
import { selectIsReset } from '../../common/selectors/chat';
import { selectHideStatusBanner, selectStatusBanner } from '../../common/selectors/header';
import { renderModel } from '../../util/renderModel';
import { FloatingBanner, FloatingBannerOrigin } from '../FloatingBanner/FloatingBanner';
import s from './StatusBanner.scss';

const isStudioBanner = renderModel.isStudioEditor();
const isCopilot = renderModel.isCopilot();

type StatusBannerProps = {
  autoHide?: boolean;
  origin?: FloatingBannerOrigin;
  hasFooter?: boolean;
  isUnifiedExperience?: boolean;
  hasSiteSelector?: boolean;
  hasMessages?: boolean;
  hasDirectSupportButton?: boolean;
};

export const StatusBanner: FC<StatusBannerProps> = memo(
  ({
    autoHide,
    origin = FloatingBannerOrigin.Home,
    hasFooter,
    isUnifiedExperience,
    hasSiteSelector,
    hasMessages,
    hasDirectSupportButton,
  }) => {
    const store = useStore();
    const dispatch = useDispatch();
    const statusBanner = useSelector(selectStatusBanner);
    const hideStatusBanner = useSelector(selectHideStatusBanner);
    const { t } = useTranslation<TLocalesKeys>();

    const { itemRef } = useBiOnVisibleItem<HTMLDivElement>(
      SectionEventSource.STATUS_BANNER,
      statusBanner?.textKey,
      ButtonClicked.StatusBanner,
    );

    const onStatusBannerClick = useCallback(
      async (e) => {
        if (e.target?.tagName === 'A') {
          const state = store.getState();
          logChatbotStatusBannerStatusBannerClicked(state, statusBanner!.biData);
          await biLogger.helpWidgetItemClickNew(state, {
            clicked_item_type: ButtonClicked.StatusBanner,
            clicked_text: statusBanner?.textKey || statusBanner?.incidentName || 'status_banner',
            widget_page_type: 'home',
            presented_content: JSON.stringify(statusBanner?.biData),
          });
        }
      },
      [store, statusBanner],
    );

    const onStatusBannerClose = useCallback(() => {
      if (statusBanner) {
        const state = store.getState();
        logChatbotStatusBannerStatusBannerDismissed(state, statusBanner?.biData);
        dispatch(headerActions.resetStatusBanner());
      }
    }, [dispatch, statusBanner]);

    useEffect(() => {
      if (statusBanner) {
        const state = store.getState();
        logChatbotStatusBannerStatusBannerShowed(state, statusBanner.biData);
      }
    }, [statusBanner]);

    const isReset = useSelector(selectIsReset);

    if (!statusBanner || isCopilot || isReset) {
      return null;
    }

    return (
      <div ref={itemRef} className={classNames({ [s.studioStatusBanner]: isStudioBanner })}>
        <FloatingBanner
          theme={statusBanner.theme as NotificationTheme}
          isBannerHidden={hideStatusBanner}
          onBannerClose={onStatusBannerClose}
          autoHide={autoHide}
          origin={origin}
          hasFooter={hasFooter}
          isUnifiedExperience={true}
          hasSiteSelector={hasSiteSelector}
          hasMessages={hasMessages}
          hasDirectSupportButton={hasDirectSupportButton}
        >
          <span
            className={s.statusBannerContent}
            onClick={onStatusBannerClick}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                t(`status-banner.${statusBanner?.textKey}`, {
                  name: statusBanner?.incidentName,
                }),
              ),
            }}
          />
        </FloatingBanner>
      </div>
    );
  },
);
